import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { AppDispatch } from "../store";
import {
  getExpressOrdersByDateAPI,
  getExpressSummaryOrdersByDateAPI,
  getWalletsListApi,
  getWalletBalanceApi,
  getWalletLogsApi,
  updateWalletBalanceApi,
} from "app/services/wallets";
import {
  WalletBalanceApiPayload,
  WalletExpressOrderInterace,
  WalletExpressSummaryOrderInterace,
  WalletInterface,
  WalletLogsDataItem,
  WalletLogsFilters,
} from "app/types/wallets";
import { alertMessage } from "../actions/common";
import {
  dateToStrFormat,
  formatNumberWithCommas,
  paginationDataConversion,
} from "app/common/Filter/utils";
import { PagingFormatedLF } from "app/types";
import { createQuery } from "app/utils/constants/common";
import { RootState } from "../reducers/hooks";
import { CreditPackageInterface, PackagesInterface } from "app/types/package";
import { errorHandler } from "app/shared/Service/errorHandler";

interface WalletsFilters {
  search?: string;
  bgte?: number;
  blte?: number;
  page?: number;
  pageSize?: number;
}

interface TopupClientDetails {
  id: number;
  name: string;
  email: string;
  phoneNumber: string;
}

interface TopupWalletDetails {
  pointPackage: PackagesInterface;
  creditPackage: CreditPackageInterface;
  client: TopupClientDetails;
}

interface WalletBalance {
  clientId: number;
  balance: number;
  validityDate: Date;
}
interface InitialState {
  filters: WalletsFilters | null;
  walletsList: WalletInterface[];
  walletsListPagination: PagingFormatedLF | null;
  isWalletLoading: boolean;
  topupWalletDetails: TopupWalletDetails | null;
  selectedExpressOrdersDate: Date;
  expressOrderList: WalletExpressOrderInterace[];
  expressSummaryOrderList: WalletExpressSummaryOrderInterace[];
  isExpressOrdersLoading: boolean;
  isWalletLogsLoading: boolean;
  walletBalance: WalletBalance;
  walletLogs: WalletLogsDataItem[];
  walletLogsPagination: PagingFormatedLF | null;
  walletLogsFilters: WalletLogsFilters | null;
}

const initialState: InitialState = {
  filters: {
    pageSize: 25,
    page: 1,
  },
  walletsList: [],
  walletsListPagination: null,
  isWalletLoading: false,
  topupWalletDetails: null,
  selectedExpressOrdersDate: new Date(),
  expressOrderList: [],
  expressSummaryOrderList: [],
  isExpressOrdersLoading: false,
  walletBalance: null,
  isWalletLogsLoading: false,
  walletLogs: [],
  walletLogsPagination: null,
  walletLogsFilters: {
    pageSize: 25,
    page: 1,
  },
};

export const walletsSlice = createSlice({
  name: "wallets",
  initialState,
  reducers: {
    setWalletsFilters: (
      state,
      action: PayloadAction<WalletsFilters | null>
    ) => {
      state.filters = action.payload;
    },
    setWalletsList: (
      state,
      action: PayloadAction<{
        walletsList: WalletInterface[];
        pagination: PagingFormatedLF | null;
      }>
    ) => {
      state.walletsList = action.payload.walletsList;
      state.walletsListPagination = action.payload.pagination;
    },
    setWalletsLogs: (
      state,
      action: PayloadAction<{
        walletLogs: WalletLogsDataItem[];
        pagination: PagingFormatedLF | null;
      }>
    ) => {
      state.walletLogs = action.payload.walletLogs;
      state.walletLogsPagination = action.payload.pagination;
    },
    setWalletsLogsFilters: (
      state,
      action: PayloadAction<WalletLogsFilters | null>
    ) => {
      state.walletLogsFilters = action.payload;
    },
    setWalletsLoading: (state, action: PayloadAction<boolean>) => {
      state.isWalletLoading = action.payload;
    },

    setTopUpWalletDetails: (
      state,
      action: PayloadAction<Partial<TopupWalletDetails>>
    ) => {
      state.topupWalletDetails = {
        ...state.topupWalletDetails,
        ...action.payload,
      };
    },
    clearTopUpWalletDetails: (state) => {
      state.topupWalletDetails = null;
    },
    setSelectedExpressOrdersDate: (state, action: PayloadAction<Date>) => {
      state.selectedExpressOrdersDate = action.payload;
    },

    setExpressOrderList: (
      state,
      action: PayloadAction<{
        list: WalletExpressOrderInterace[];
      }>
    ) => {
      state.expressOrderList = action.payload.list;
    },

    setExpressSummaryOrderList: (
      state,
      action: PayloadAction<{
        list: WalletExpressSummaryOrderInterace[];
      }>
    ) => {
      state.expressSummaryOrderList = action.payload.list;
    },

    setExpressOrdersLoading: (state, action: PayloadAction<boolean>) => {
      state.isExpressOrdersLoading = action.payload;
    },
    setWalletsLogsLoading: (state, action: PayloadAction<boolean>) => {
      state.isWalletLogsLoading = action.payload;
    },
    setWalletBalanceData: (state, action: PayloadAction<WalletBalance>) => {
      state.walletBalance = action.payload;
    },
  },
});

// Action creators are generated for each case reducer function
export const {
  setWalletsFilters,
  setWalletsList,
  setWalletsLoading,
  setTopUpWalletDetails,
  clearTopUpWalletDetails,
  setSelectedExpressOrdersDate,
  setExpressOrderList,
  setExpressSummaryOrderList,
  setExpressOrdersLoading,
  setWalletBalanceData,
  setWalletsLogs,
  setWalletsLogsFilters,
  setWalletsLogsLoading,
} = walletsSlice.actions;
export default walletsSlice.reducer;

export const getWalletsApiAction = () => {
  return async (dispatch: AppDispatch, getState: () => RootState) => {
    try {
      const { filters } = getState().wallets;
      const filter = { ...filters };
      const query = "?" + createQuery({ filter: filter });

      dispatch(setWalletsLoading(true));
      const response = await getWalletsListApi(query);
      dispatch(setWalletsLoading(false));
      const data = response.data.data;
      const pagination = response.data.paging;
      const paginatedData = paginationDataConversion(pagination);
      dispatch(
        setWalletsList({
          walletsList: data,
          pagination: paginatedData,
        })
      );
    } catch (error) {
      dispatch(setWalletsLoading(false));
      dispatch(alertMessage("Error fetching wallets", "error"));
    }
  };
};

export const getWalletExpressOrderListAction = (clientId: number) => {
  return async (dispatch: AppDispatch, getState: () => RootState) => {
    try {
      const { selectedExpressOrdersDate } = getState().wallets;
      const formattedDate = dateToStrFormat(selectedExpressOrdersDate);

      dispatch(setExpressOrdersLoading(true));
      const { data } = await getExpressOrdersByDateAPI(clientId, formattedDate);
      dispatch(setExpressOrdersLoading(false));

      const expressOrderList = data.data;
      const exchangeRates = data.included.exchangeRates;

      // Update list
      const updatedExpressOrderList = expressOrderList.map((order) => {
        const exchangeRate = exchangeRates.find(
          (item) => item.storeId === order.storeId
        );
        const price = {
          amount: formatNumberWithCommas(order.paidAmount * exchangeRate.rate),
          code: "KD",
        };

        return {
          ...order,
          price,
        };
      });

      dispatch(
        setExpressOrderList({
          list: updatedExpressOrderList,
        })
      );
    } catch (error) {
      const message = errorHandler(error);
      dispatch(setExpressOrdersLoading(false));
      dispatch(alertMessage(message, "error"));
    }
  };
};

export const getWalletExpressSummaryOrderListAction = (clientId: number) => {
  return async (dispatch: AppDispatch, getState: () => RootState) => {
    try {
      const { selectedExpressOrdersDate } = getState().wallets;
      const formattedDate = dateToStrFormat(selectedExpressOrdersDate);
      dispatch(setExpressOrdersLoading(true));
      const { data } = await getExpressSummaryOrdersByDateAPI(
        clientId,
        formattedDate
      );
      dispatch(setExpressOrdersLoading(false));

      const expressOrderList = data.data;

      dispatch(
        setExpressSummaryOrderList({
          list: expressOrderList,
        })
      );
    } catch (error) {
      const message = errorHandler(error);
      dispatch(setExpressOrdersLoading(false));
      dispatch(alertMessage(message, "error"));
    }
  };
};

export const getWalletBalanceApiAction = (clientId: number) => {
  return async (dispatch: AppDispatch) => {
    try {
      const response = await getWalletBalanceApi(clientId);
      const data = response.data.data;
      dispatch(setWalletBalanceData(data));
    } catch (error) {
      dispatch(alertMessage("Error fetching Balance", "error"));
    }
  };
};

export const updateWalletBalanceApiAction = (
  clientId: number,
  payload: WalletBalanceApiPayload
) => {
  return async (dispatch: AppDispatch) => {
    try {
      const response = await updateWalletBalanceApi(clientId, payload);
      if (response.data) {
        dispatch(alertMessage("Updated successfully", "success"));
        dispatch(getWalletBalanceApiAction(response.data.data.clientId));
      }
    } catch (error) {
      dispatch(alertMessage("Error updating Balance", "error"));
    }
  };
};

export const getWalletLogsApiAction = (clientId: number) => {
  return async (dispatch: AppDispatch, getState: () => RootState) => {
    try {
      const { walletLogsFilters } = getState().wallets;
      const filters = walletLogsFilters ?? { page: 1, pageSize: 25 };
      const filter = { ...filters, logType: "All" };
      const query = "?" + createQuery({ filter });

      dispatch(setWalletsLogsLoading(true));
      const response = await getWalletLogsApi(clientId, query);
      dispatch(setWalletsLogsLoading(false));

      const data = response.data.data;
      const users = response.data.included?.users ?? [];
      const pagination = response.data.paging;
      const paginatedData = paginationDataConversion(pagination);

      const updatedLogs = data.map((log) => {
        const user = log.userId
          ? users.find((user) => user.id === log.userId)
          : null;
        return {
          ...log,
          user: user ?? null,
        };
      });

      dispatch(
        setWalletsLogs({
          walletLogs: updatedLogs,
          pagination: paginatedData,
        })
      );
    } catch (error) {
      dispatch(setWalletsLogsLoading(false));
      dispatch(alertMessage("Error fetching Data", "error"));
    }
  };
};
